import React from 'react';
import { Check, AlertTriangle } from 'lucide-react';
import { STEPS, stepIndex } from './stepConfig';

/**
 * Desktop: icon circles with labels. Mobile: "Step 2 of 5 · Title" + progress bar.
 */
const ApplicationStepper = ({ currentId, completedIds = [], flaggedIds = [], onSelect, canSelect = () => false }) => {
  const currentIndex = Math.max(0, stepIndex(currentId));
  const percent = Math.round(((currentIndex + 1) / STEPS.length) * 100);
  const current = STEPS[currentIndex];

  return (
    <nav aria-label="Application steps" className="w-full">
      {/* Mobile */}
      <div className="sm:hidden">
        <div className="flex items-baseline justify-between">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Step {currentIndex + 1} of {STEPS.length}</p>
          <p className="text-xs text-gray-500">{percent}% through</p>
        </div>
        <p className="mt-0.5 text-base font-semibold text-gray-900">{current?.title}</p>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} aria-label="Application progress">
          <div className="h-full rounded-full bg-brand-mid transition-all duration-300" style={{ width: `${percent}%` }} />
        </div>
      </div>

      {/* Desktop */}
      <ol className="hidden sm:grid sm:grid-cols-5 sm:gap-2">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isCurrent = step.id === currentId;
          const isComplete = completedIds.includes(step.id) && !isCurrent;
          const isFlagged = flaggedIds.includes(step.id);
          const selectable = canSelect(step.id) && !isCurrent;
          const circleClass = isCurrent
            ? 'border-brand-mid bg-white text-brand-mid ring-4 ring-blue-100'
            : isFlagged
              ? 'border-amber-400 bg-amber-50 text-amber-700'
              : isComplete
                ? 'border-brand-dark bg-brand-dark text-white'
                : 'border-gray-300 bg-white text-gray-400';
          const labelClass = isCurrent ? 'text-brand-dark font-semibold' : isComplete || isFlagged ? 'text-gray-800' : 'text-gray-500';

          return (
            <li key={step.id} className="flex flex-col items-center text-center">
              <button
                type="button"
                onClick={() => selectable && onSelect?.(step.id)}
                disabled={!selectable}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`${step.title}${isComplete ? ', completed' : ''}${isFlagged ? ', needs changes' : ''}`}
                className={`flex h-12 w-12 items-center justify-center rounded-full border-2 transition-colors ${circleClass} ${selectable ? 'cursor-pointer hover:ring-4 hover:ring-blue-100' : 'cursor-default'} focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-200`}
              >
                {isComplete && !isFlagged ? <Check className="h-5 w-5" strokeWidth={3} aria-hidden="true" />
                  : isFlagged ? <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                    : <Icon className="h-5 w-5" aria-hidden="true" />}
              </button>
              <span className={`mt-2 text-xs sm:text-sm ${labelClass}`}>{step.title}</span>
              <span className="sr-only">{index + 1} of {STEPS.length}</span>
            </li>
          );
        })}
      </ol>
      <div className="mt-3 hidden h-2 w-full overflow-hidden rounded-full bg-gray-200 sm:block" aria-hidden="true">
        <div className="h-full rounded-full bg-gradient-to-r from-brand-dark to-brand-mid transition-all duration-300" style={{ width: `${percent}%` }} />
      </div>
    </nav>
  );
};

export default ApplicationStepper;
